/**
 * LERA Premium Gallery Viewer — Sprint 8.4C Media UX Polish
 * Fullscreen immersive viewer. Subscribes to LeraGalleryNav — no second gallery state.
 * Future media types (video / model / 360 / AR) register via LeraPgvMediaRegistry.
 */

(function () {
  const DESKTOP_MQL = '(min-width: 750px)';
  const OPEN_CLASS = 'lera-pgv--open';
  const CLOSING_CLASS = 'lera-pgv--closing';
  const UI_HIDDEN_CLASS = 'lera-pgv--ui-hidden';
  const MEDIA_MS = 200;
  const controllers = new WeakMap();
  let activeController = null;

  const INDICATOR_SVG = {
    play: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.14v13.72L19 12 8 5.14z"/></svg>',
    cube: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z"/><path d="M12 12l8-4.5M12 12v9M12 12L4 7.5"/></svg>',
    '360': '<svg width="18" height="12" viewBox="0 0 24 16" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><ellipse cx="12" cy="8" rx="9" ry="5"/><path d="M7 8c0 1.5 2.2 2.7 5 2.7s5-1.2 5-2.7"/></svg>',
  };

  const defaultMediaRegistry = {
    image: { kind: 'image', indicator: null, render: 'image' },
    video: { kind: 'video', indicator: 'play', render: 'video' },
    external_video: { kind: 'video', indicator: 'play', render: 'video' },
    model: { kind: 'model', indicator: 'cube', render: 'image' },
    model_3d: { kind: 'model', indicator: 'cube', render: 'image' },
    '360': { kind: '360', indicator: '360', render: 'image' },
    multi_angle: { kind: '360', indicator: '360', render: 'image' },
    ar: { kind: 'ar', indicator: 'cube', render: 'image' },
    glb: { kind: 'model', indicator: 'cube', render: 'image' },
    usdz: { kind: 'ar', indicator: 'cube', render: 'image' },
  };

  const mediaRegistry = { ...defaultMediaRegistry };

  window.LeraPgvMediaRegistry = {
    register(type, definition) {
      if (!type || !definition) return;
      mediaRegistry[String(type)] = {
        kind: definition.kind || 'image',
        indicator: definition.indicator ?? null,
        render: definition.render || 'image',
        ...definition,
      };
    },
    resolve(type) {
      return mediaRegistry[type] || mediaRegistry.image;
    },
    list() {
      return { ...mediaRegistry };
    },
  };

  function readConfig(root) {
    const productInfo = root.closest('product-info') || document.querySelector('product-info');
    const node = productInfo?.querySelector('[data-lera-gallery-viewer-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  function getGalleryForRoot(root) {
    return root.closest('product-info')?.querySelector('media-gallery.lera-product-gallery') || null;
  }

  function getSlide(gallery, key) {
    return gallery?.querySelector(`.product__media-item[data-media-id="${key}"]`) || null;
  }

  function getSlideMediaType(slide) {
    return slide?.querySelector('[data-media-type]')?.dataset.mediaType || 'image';
  }

  function getSlideImage(slide) {
    return (
      slide?.querySelector('.product__media img') ||
      slide?.querySelector('.deferred-media__poster img') ||
      slide?.querySelector('img') ||
      null
    );
  }

  function getThumbSource(gallery, key) {
    return (
      gallery.querySelector(`.lera-gallery-thumb[data-target="${key}"] img`) ||
      getSlideImage(getSlide(gallery, key))
    );
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function createIndicatorEl(type) {
    const svg = INDICATOR_SVG[type];
    if (!svg) return null;
    const wrap = document.createElement('span');
    wrap.className = 'lera-pgv__thumb-badge';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = svg;
    return wrap;
  }

  class LeraPremiumGalleryViewerController {
    constructor(root) {
      this.root = root;
      this.config = readConfig(root);
      this.gallery = getGalleryForRoot(root);
      this.mql = window.matchMedia(DESKTOP_MQL);
      this.dialog = root.querySelector('[data-lera-pgv-dialog]');
      this.backdrop = root.querySelector('[data-lera-pgv-backdrop]');
      this.stage = root.querySelector('[data-lera-pgv-stage]');
      this.skeletonEl = root.querySelector('[data-lera-pgv-skeleton]');
      this.zoomEl = root.querySelector('[data-lera-pgv-zoom]');
      this.imageEl = root.querySelector('[data-lera-pgv-image]');
      this.videoEl = root.querySelector('[data-lera-pgv-video]');
      this.badgeEl = root.querySelector('[data-lera-pgv-badge]');
      this.counterEl = root.querySelector('[data-lera-pgv-counter]');
      this.counterCurrentEl = root.querySelector('[data-lera-pgv-counter-current]');
      this.counterTotalEl = root.querySelector('[data-lera-pgv-counter-total]');
      this.statusEl = root.querySelector('[data-lera-pgv-status]');
      this.thumbsEl = root.querySelector('[data-lera-pgv-thumbs]');
      this.closeBtn = root.querySelector('[data-lera-pgv-close]');
      this.prevBtn = root.querySelector('[data-lera-pgv-prev]');
      this.nextBtn = root.querySelector('[data-lera-pgv-next]');
      this.isOpen = false;
      this.lastFocused = null;
      this.hideTimer = null;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.preloadImgs = [];
      this.thumbsSignature = '';
      this.activeMediaKey = null;
      this.mediaToken = 0;
      this.fadeTimer = null;
      this.zoom = { scale: 1, x: 0, y: 0 };
      this.drag = null;
      this.pinch = null;
      this.lastTap = 0;
      this.zoomFrame = null;
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPointerActivity = this.onPointerActivity.bind(this);
      this.onNavigate = this.onNavigate.bind(this);
      this.onVariant = this.onVariant.bind(this);
      this.onWheelZoom = this.onWheelZoom.bind(this);
      this.onZoomPointerDown = this.onZoomPointerDown.bind(this);
      this.onZoomPointerMove = this.onZoomPointerMove.bind(this);
      this.onZoomPointerUp = this.onZoomPointerUp.bind(this);
      this.onZoomDblClick = this.onZoomDblClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
    }

    bind() {
      if (!this.config?.enabled || !this.gallery || !this.dialog) return;
      if (controllers.has(this.root)) return;
      controllers.set(this.root, this);

      this.applyThemeVars();
      this.bindTriggers();
      this.bindChrome();

      this.gallery.addEventListener('lera:gallery:navigate', this.onNavigate);
      document.addEventListener('lera:variant:selected', this.onVariant);
      document.addEventListener('variant:selected', this.onVariant);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event?.data?.sectionId) !== String(this.config.sectionId)) return;
          if (this.isOpen) this.syncFromGallery();
        });
      }

      this.mql.addEventListener('change', () => {
        if (this.isOpen) this.syncFromGallery();
      });
    }

    applyThemeVars() {
      const c = this.config;
      this.root.style.setProperty('--lera-pgv-anim', `${c.animSpeed ?? 240}ms`);
      this.root.style.setProperty('--lera-pgv-media', `${MEDIA_MS}ms`);
      let bgOpacity = Number(c.bgOpacity ?? 58);
      if (bgOpacity > 75) bgOpacity = 58;
      this.root.style.setProperty('--lera-pgv-bg-opacity', String(bgOpacity / 100));
      this.root.style.setProperty('--lera-pgv-blur', '12px');
      this.root.style.setProperty('--lera-pgv-arrow-size', `${c.arrowSize ?? 52}px`);
      this.root.style.setProperty('--lera-pgv-arrow-opacity', String((c.arrowOpacity ?? 60) / 100));
      this.root.style.setProperty(
        '--lera-pgv-arrow-hover-opacity',
        String((c.arrowHoverOpacity ?? 100) / 100)
      );
      this.root.style.setProperty('--lera-pgv-thumb-size', `${c.thumbSize ?? 64}px`);
      this.root.style.setProperty('--lera-pgv-thumb-gap', `${c.thumbGap ?? 8}px`);
      this.root.classList.toggle('lera-pgv--safe-area', Boolean(c.safeArea));
    }

    bindTriggers() {
      const gallery = this.gallery;

      gallery.addEventListener(
        'click',
        (event) => {
          if (!this.config.enabled) return;
          if (event.target.closest('[data-lera-gallery-nav]')) return;
          if (event.target.closest('[data-lera-gallery-overflow]')) return;

          const toggle = event.target.closest('.product__media-toggle');
          if (toggle && gallery.querySelector('.lera-gallery-layout__stage')?.contains(toggle)) {
            event.preventDefault();
            event.stopPropagation();
            const mediaId = toggle.getAttribute('data-media-id');
            const sectionId = this.config.sectionId;
            const key = mediaId
              ? mediaId.includes(String(sectionId))
                ? mediaId
                : `${sectionId}-${mediaId}`
              : window.LeraGalleryNav?.getActiveKey(gallery);
            this.open({ gallery, targetKey: key });
          }
        },
        true
      );

      gallery.addEventListener('click', (event) => {
        if (!this.config.enabled) return;
        if (event.target.closest('[data-lera-gallery-overflow]')) return;
        if (event.target.closest('[data-lera-gallery-nav]')) return;

        const thumb = event.target.closest('.lera-gallery-thumb');
        if (!thumb || thumb.classList.contains('lera-gallery-overflow--slot-hidden')) return;
        if (!event.target.closest('button')) return;

        this.open({ gallery, targetKey: thumb.dataset.target });
      });
    }

    bindChrome() {
      this.closeBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });

      this.backdrop?.addEventListener('click', () => this.close());

      this.stage?.addEventListener('click', (event) => {
        if (event.target === this.stage) this.close();
      });

      this.prevBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        this.revealUi();
        window.LeraGalleryNav?.prev(this.gallery);
      });

      this.nextBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        this.revealUi();
        window.LeraGalleryNav?.next(this.gallery);
      });

      this.thumbsEl?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-lera-pgv-thumb]');
        if (!btn) return;
        event.preventDefault();
        this.revealUi();
        window.LeraGalleryNav?.goTo(this.gallery, btn.dataset.leraPgvThumb);
      });

      this.dialog?.addEventListener('mousemove', this.onPointerActivity);
      this.dialog?.addEventListener('pointerdown', this.onPointerActivity);

      this.stage?.addEventListener('wheel', this.onWheelZoom, { passive: false });
      this.zoomEl?.addEventListener('pointerdown', this.onZoomPointerDown);
      this.zoomEl?.addEventListener('dblclick', this.onZoomDblClick);
      window.addEventListener('pointermove', this.onZoomPointerMove);
      window.addEventListener('pointerup', this.onZoomPointerUp);
      window.addEventListener('pointercancel', this.onZoomPointerUp);

      if (this.config.swipeEnabled !== false) {
        this.stage?.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.stage?.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.stage?.addEventListener('touchend', this.onTouchEnd, { passive: false });
      }
    }

    lockPageScroll() {
      this.savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('lera-pgv-lock');
      document.body.classList.add('lera-pgv-lock-body');
      document.body.style.top = `-${this.savedScrollY}px`;
    }

    unlockPageScroll() {
      document.documentElement.classList.remove('lera-pgv-lock');
      document.body.classList.remove('lera-pgv-lock-body');
      document.body.style.top = '';
      const y = this.savedScrollY || 0;
      window.scrollTo(0, y);
      this.savedScrollY = 0;
    }

    mountFullscreenLayer() {
      if (this.root.parentElement === document.body) return;
      this.homeParent = this.root.parentElement;
      this.homeNextSibling = this.root.nextSibling;
      document.body.appendChild(this.root);
      this.root.classList.add('lera-pgv--ported');
    }

    restoreHomeLayer() {
      this.root.classList.remove('lera-pgv--ported');
      if (!this.homeParent) return;
      if (this.homeNextSibling && this.homeNextSibling.parentNode === this.homeParent) {
        this.homeParent.insertBefore(this.root, this.homeNextSibling);
      } else {
        this.homeParent.appendChild(this.root);
      }
      this.homeParent = null;
      this.homeNextSibling = null;
    }

    open(options = {}) {
      if (!this.config?.enabled || !this.gallery) return;

      if (options.gallery) this.gallery = options.gallery;
      if (options.targetKey) window.LeraGalleryNav?.goTo(this.gallery, options.targetKey);

      if (this.isOpen) {
        this.syncFromGallery();
        return;
      }

      this.isOpen = true;
      activeController = this;
      this.lastFocused = document.activeElement;

      this.mountFullscreenLayer();
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      this.lockPageScroll();

      requestAnimationFrame(() => {
        this.root.classList.add(OPEN_CLASS);
        this.syncFromGallery();
        this.dialog?.focus({ preventScroll: true });
        this.revealUi();
      });

      document.addEventListener('keydown', this.onKeyDown);
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      if (activeController === this) activeController = null;

      this.clearHideTimer();
      this.clearFadeTimer();
      this.pauseViewerMedia();
      this.resetZoom();
      this.hideSkeleton();
      this.root.classList.add(CLOSING_CLASS);
      this.root.classList.remove(OPEN_CLASS, UI_HIDDEN_CLASS);
      document.removeEventListener('keydown', this.onKeyDown);
      this.unlockPageScroll();

      const duration = this.config.animSpeed ?? 240;
      window.setTimeout(() => {
        this.root.classList.remove(CLOSING_CLASS);
        this.root.hidden = true;
        this.root.setAttribute('aria-hidden', 'true');
        this.clearVideo();
        this.restoreHomeLayer();
        this.lastFocused?.focus?.({ preventScroll: true });
      }, duration);
    }

    onNavigate() {
      if (!this.isOpen) return;
      this.resetZoom();
      this.syncFromGallery();
      this.revealUi();
    }

    onVariant(event) {
      const productId = this.gallery?.closest('product-info')?.dataset?.productId;
      const detailId = event?.detail?.product?.id;
      if (detailId && productId && String(detailId) !== String(productId)) return;
      if (!this.isOpen) return;
      requestAnimationFrame(() => this.syncFromGallery());
    }

    syncFromGallery() {
      if (!this.gallery) return;

      const keys = window.LeraGalleryNav?.getMediaKeys(this.gallery) || [];
      const activeKey = window.LeraGalleryNav?.getActiveKey(this.gallery) || keys[0];
      const index = Math.max(0, keys.indexOf(activeKey));

      this.renderStage(activeKey, index, keys.length);
      this.renderCounter(index, keys.length);
      this.renderThumbs(keys, activeKey);
      this.preloadAdjacent(keys, index);
      this.updateArrowState(keys.length);
    }

    renderStage(key, index, total) {
      if (!key) return;
      const slide = getSlide(this.gallery, key);
      const type = getSlideMediaType(slide);
      const media = window.LeraPgvMediaRegistry.resolve(type);

      this.pauseViewerMedia();
      this.setMediaBadge(media.indicator);

      if (media.render === 'video') {
        this.hideSkeleton();
        this.renderVideo(slide);
      } else {
        this.renderImage(slide, key);
      }

      if (this.statusEl) {
        this.statusEl.textContent = `Image ${index + 1} of ${total}`;
      }
    }

    setMediaBadge(indicator) {
      if (!this.badgeEl) return;
      if (!indicator) {
        this.badgeEl.hidden = true;
        this.badgeEl.dataset.leraPgvBadgeType = '';
        this.badgeEl.innerHTML = '';
        return;
      }
      const svg = INDICATOR_SVG[indicator];
      if (!svg) {
        this.badgeEl.hidden = true;
        return;
      }
      this.badgeEl.dataset.leraPgvBadgeType = indicator;
      this.badgeEl.innerHTML = svg;
      this.badgeEl.hidden = false;
    }

    showSkeleton() {
      if (!this.skeletonEl) return;
      this.skeletonEl.hidden = false;
      requestAnimationFrame(() => this.skeletonEl.classList.add('is-visible'));
    }

    hideSkeleton() {
      if (!this.skeletonEl) return;
      this.skeletonEl.classList.remove('is-visible');
      const hide = () => {
        if (!this.skeletonEl.classList.contains('is-visible')) this.skeletonEl.hidden = true;
      };
      if (prefersReducedMotion()) hide();
      else window.setTimeout(hide, MEDIA_MS);
    }

    clearFadeTimer() {
      if (this.fadeTimer) {
        clearTimeout(this.fadeTimer);
        this.fadeTimer = null;
      }
    }

    renderImage(slide, key) {
      const source = getSlideImage(slide);
      if (!this.imageEl || !source) return;

      this.clearVideo();
      this.resetZoom();
      this.videoEl.hidden = true;
      this.imageEl.hidden = false;

      const nextSrc = source.currentSrc || source.src;
      const nextSrcset = source.srcset || '';
      const sameAsset =
        key &&
        key === this.activeMediaKey &&
        this.imageEl.src &&
        (this.imageEl.currentSrc === nextSrc || this.imageEl.src === nextSrc) &&
        this.imageEl.classList.contains('lera-pgv__image--ready');

      if (sameAsset) {
        this.hideSkeleton();
        return;
      }

      this.mediaToken += 1;
      const token = this.mediaToken;
      this.activeMediaKey = key || null;
      this.clearFadeTimer();

      // Fade out current → skeleton holds the frame → fade in next (no blank flash).
      this.showSkeleton();
      this.imageEl.classList.add('lera-pgv__image--exiting');
      this.imageEl.classList.remove('lera-pgv__image--ready');

      const applySource = () => {
        if (token !== this.mediaToken) return;

        this.imageEl.classList.remove('lera-pgv__image--ready', 'lera-pgv__image--exiting');

        const onLoad = () => {
          if (token !== this.mediaToken) return;
          this.imageEl.removeEventListener('load', onLoad);
          requestAnimationFrame(() => {
            if (token !== this.mediaToken) return;
            this.imageEl.classList.add('lera-pgv__image--ready');
            this.hideSkeleton();
          });
        };

        this.imageEl.addEventListener('load', onLoad);

        if (nextSrcset) this.imageEl.srcset = nextSrcset;
        else this.imageEl.removeAttribute('srcset');
        this.imageEl.sizes = '90vw';
        this.imageEl.alt = source.alt || '';
        this.imageEl.fetchPriority = 'high';
        this.imageEl.decoding = 'async';
        this.imageEl.src = nextSrc;

        if (this.imageEl.complete && this.imageEl.naturalWidth > 0) onLoad();
      };

      // Swap source after fade-out starts so the bitmap never pops mid-opacity.
      if (prefersReducedMotion()) applySource();
      else this.fadeTimer = window.setTimeout(applySource, Math.min(MEDIA_MS, 120));
    }

    resetZoom() {
      this.zoom = { scale: 1, x: 0, y: 0 };
      this.drag = null;
      this.pinch = null;
      this.applyZoomTransform();
    }

    scheduleZoomPaint() {
      if (this.zoomFrame) return;
      this.zoomFrame = requestAnimationFrame(() => {
        this.zoomFrame = null;
        this.applyZoomTransform();
      });
    }

    applyZoomTransform() {
      if (!this.zoomEl) return;
      const { scale, x, y } = this.zoom;
      this.zoomEl.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      this.zoomEl.classList.toggle('is-zoomed', scale > 1.01);
      if (scale <= 1.01) this.zoomEl.classList.remove('is-dragging');
    }

    clampPan() {
      if (this.zoom.scale <= 1) {
        this.zoom.x = 0;
        this.zoom.y = 0;
        return;
      }
      const stage = this.stage?.getBoundingClientRect();
      if (!stage) return;
      const maxX = (stage.width * (this.zoom.scale - 1)) / 2;
      const maxY = (stage.height * (this.zoom.scale - 1)) / 2;
      this.zoom.x = Math.max(-maxX, Math.min(maxX, this.zoom.x));
      this.zoom.y = Math.max(-maxY, Math.min(maxY, this.zoom.y));
    }

    setZoomAt(nextScale, clientX, clientY) {
      const prev = this.zoom.scale;
      const scale = Math.max(1, Math.min(4, nextScale));
      if (scale === prev) return;

      if (scale === 1) {
        this.zoom = { scale: 1, x: 0, y: 0 };
        this.scheduleZoomPaint();
        return;
      }

      const stage = this.stage?.getBoundingClientRect();
      if (stage && clientX != null && clientY != null && prev > 0) {
        const ox = clientX - (stage.left + stage.width / 2);
        const oy = clientY - (stage.top + stage.height / 2);
        const ratio = scale / prev;
        this.zoom.x = ox - (ox - this.zoom.x) * ratio;
        this.zoom.y = oy - (oy - this.zoom.y) * ratio;
      }

      this.zoom.scale = scale;
      this.clampPan();
      this.scheduleZoomPaint();
    }

    toggleZoom(clientX, clientY) {
      if (this.zoom.scale > 1.05) this.setZoomAt(1);
      else this.setZoomAt(2, clientX, clientY);
    }

    onWheelZoom(event) {
      if (!this.isOpen || this.imageEl?.hidden) return;
      event.preventDefault();
      this.revealUi();
      const delta = event.deltaY > 0 ? -0.18 : 0.18;
      this.setZoomAt(this.zoom.scale + delta, event.clientX, event.clientY);
    }

    onZoomDblClick(event) {
      if (this.imageEl?.hidden) return;
      event.preventDefault();
      this.revealUi();
      this.toggleZoom(event.clientX, event.clientY);
    }

    onZoomPointerDown(event) {
      if (!this.isOpen || this.imageEl?.hidden) return;
      if (event.pointerType === 'touch') return;
      if (this.zoom.scale <= 1.01) return;
      event.preventDefault();
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: this.zoom.x,
        originY: this.zoom.y,
      };
      this.zoomEl?.classList.add('is-dragging');
      this.zoomEl?.setPointerCapture?.(event.pointerId);
      this.revealUi();
    }

    onZoomPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.zoom.x = this.drag.originX + (event.clientX - this.drag.startX);
      this.zoom.y = this.drag.originY + (event.clientY - this.drag.startY);
      this.clampPan();
      this.scheduleZoomPaint();
    }

    onZoomPointerUp(event) {
      if (!this.drag || (event && event.pointerId !== this.drag.pointerId)) return;
      this.drag = null;
      this.zoomEl?.classList.remove('is-dragging');
    }

    touchDistance(touches) {
      const a = touches[0];
      const b = touches[1];
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }

    touchCenter(touches) {
      const a = touches[0];
      const b = touches[1];
      return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
    }

    renderVideo(slide) {
      if (!this.videoEl) return;

      this.imageEl.hidden = true;
      this.imageEl.classList.remove('lera-pgv__image--ready', 'lera-pgv__image--exiting');
      this.videoEl.hidden = false;
      this.clearVideo();
      this.activeMediaKey = null;

      const deferred = slide?.querySelector('deferred-media');
      const template = deferred?.querySelector('template');

      if (template?.content) {
        this.videoEl.appendChild(template.content.cloneNode(true));
        const media = this.videoEl.querySelector('video, iframe');
        if (media?.tagName === 'VIDEO') {
          media.controls = true;
          media.play?.().catch(() => {});
        }
        return;
      }

      const poster = getSlideImage(slide);
      if (poster) {
        const img = document.createElement('img');
        img.className = 'lera-pgv__image lera-pgv__image--ready';
        img.src = poster.currentSrc || poster.src;
        img.alt = poster.alt || '';
        this.videoEl.appendChild(img);
      }
      deferred?.loadContent?.(false);
    }

    clearVideo() {
      if (!this.videoEl) return;
      this.pauseViewerMedia();
      this.videoEl.innerHTML = '';
    }

    pauseViewerMedia() {
      this.videoEl?.querySelectorAll('video').forEach((video) => {
        try {
          video.pause();
        } catch {
          /* noop */
        }
      });
      this.videoEl?.querySelectorAll('iframe').forEach((iframe) => {
        iframe.src = iframe.src;
      });
      window.pauseAllMedia?.();
    }

    renderCounter(index, total) {
      if (!this.counterEl || !this.config.showCounter) return;
      if (this.counterCurrentEl && this.counterTotalEl) {
        this.counterCurrentEl.textContent = String(index + 1);
        this.counterTotalEl.textContent = String(total);
        return;
      }
      this.counterEl.textContent = `${index + 1} / ${total}`;
    }

    renderThumbs(keys, activeKey) {
      if (!this.thumbsEl || !this.config.thumbsEnabled) return;

      if (!this.mql.matches) {
        if (this.thumbsEl.childNodes.length) this.thumbsEl.innerHTML = '';
        this.thumbsSignature = '';
        return;
      }

      const signature = keys.join('|');
      const total = keys.length;

      if (signature === this.thumbsSignature && this.thumbsEl.children.length === total) {
        Array.from(this.thumbsEl.children).forEach((btn, index) => {
          const key = keys[index];
          const active = key === activeKey;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-selected', active ? 'true' : 'false');
          btn.setAttribute('aria-label', `View image ${index + 1} of ${total}`);
        });
        this.thumbsEl
          .querySelector('.lera-pgv__thumb.is-active')
          ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        return;
      }

      this.thumbsSignature = signature;
      const fragment = document.createDocumentFragment();

      keys.forEach((key, index) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lera-pgv__thumb';
        btn.dataset.leraPgvThumb = key;
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-label', `View image ${index + 1} of ${total}`);
        btn.classList.toggle('is-active', key === activeKey);
        btn.setAttribute('aria-selected', key === activeKey ? 'true' : 'false');

        const slide = getSlide(this.gallery, key);
        const media = window.LeraPgvMediaRegistry.resolve(getSlideMediaType(slide));
        const source = getThumbSource(this.gallery, key);
        if (source) {
          const img = document.createElement('img');
          img.alt = '';
          img.loading = index <= 1 ? 'eager' : 'lazy';
          img.decoding = 'async';
          if ('fetchPriority' in img) {
            img.fetchPriority = index === 0 ? 'high' : index <= 2 ? 'low' : 'auto';
          }
          img.src = source.currentSrc || source.src;
          if (source.srcset) img.srcset = source.srcset;
          btn.appendChild(img);
        }

        if (media.indicator) {
          const badge = createIndicatorEl(media.indicator);
          if (badge) btn.appendChild(badge);
        }

        fragment.appendChild(btn);
      });

      this.thumbsEl.innerHTML = '';
      this.thumbsEl.appendChild(fragment);
      this.thumbsEl
        .querySelector('.lera-pgv__thumb.is-active')
        ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }

    preloadAdjacent(keys, index) {
      this.preloadImgs = [];
      const targets = [
        { key: keys[index], priority: 'high' },
        { key: keys[index - 1], priority: 'low' },
        { key: keys[index + 1], priority: 'low' },
      ];

      targets.forEach(({ key, priority }) => {
        if (!key) return;
        const source = getSlideImage(getSlide(this.gallery, key));
        if (!source) return;
        const img = new Image();
        img.decoding = 'async';
        if ('fetchPriority' in img) img.fetchPriority = priority;
        if (source.srcset) img.srcset = source.srcset;
        img.src = source.currentSrc || source.src;
        this.preloadImgs.push(img);
      });
    }

    updateArrowState(total) {
      const disabled = total <= 1;
      if (this.prevBtn) this.prevBtn.disabled = disabled;
      if (this.nextBtn) this.nextBtn.disabled = disabled;
    }

    revealUi() {
      this.root.classList.remove(UI_HIDDEN_CLASS);
      this.clearHideTimer();
      if (!this.config.autoHide || !this.isOpen) return;
      this.hideTimer = window.setTimeout(() => {
        if (this.isOpen) this.root.classList.add(UI_HIDDEN_CLASS);
      }, this.config.autoHideDelay ?? 2000);
    }

    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    }

    onPointerActivity() {
      if (this.isOpen) this.revealUi();
    }

    onKeyDown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.revealUi();
        window.LeraGalleryNav?.prev(this.gallery);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.revealUi();
        window.LeraGalleryNav?.next(this.gallery);
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onTouchStart(event) {
      if (!this.isOpen) return;
      const touches = event.touches;
      if (touches.length === 2 && !this.imageEl?.hidden) {
        event.preventDefault();
        this.pinch = {
          startDist: this.touchDistance(touches),
          startScale: this.zoom.scale,
        };
        this.drag = null;
        return;
      }

      if (touches.length === 1) {
        const touch = touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        if (this.zoom.scale > 1.01) {
          this.drag = {
            pointerId: 'touch',
            startX: touch.clientX,
            startY: touch.clientY,
            originX: this.zoom.x,
            originY: this.zoom.y,
          };
          this.zoomEl?.classList.add('is-dragging');
        }
      }
    }

    onTouchMove(event) {
      if (!this.isOpen) return;
      const touches = event.touches;

      if (touches.length === 2 && this.pinch && !this.imageEl?.hidden) {
        event.preventDefault();
        const dist = this.touchDistance(touches);
        const center = this.touchCenter(touches);
        const next = this.pinch.startScale * (dist / Math.max(this.pinch.startDist, 1));
        this.setZoomAt(next, center.x, center.y);
        this.revealUi();
        return;
      }

      if (touches.length === 1 && this.drag && this.zoom.scale > 1.01) {
        event.preventDefault();
        const touch = touches[0];
        this.zoom.x = this.drag.originX + (touch.clientX - this.drag.startX);
        this.zoom.y = this.drag.originY + (touch.clientY - this.drag.startY);
        this.clampPan();
        this.scheduleZoomPaint();
      }
    }

    onTouchEnd(event) {
      if (!this.isOpen) return;

      if (event.touches.length < 2) this.pinch = null;

      if (event.touches.length === 0) {
        const touch = event.changedTouches?.[0];
        const wasDraggingZoom = Boolean(this.drag && this.zoom.scale > 1.01);
        this.drag = null;
        this.zoomEl?.classList.remove('is-dragging');
        if (!touch) return;

        const dx = touch.clientX - this.touchStartX;
        const dy = touch.clientY - this.touchStartY;
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        const now = Date.now();
        const isTap = absX < 12 && absY < 12;

        if (isTap && !this.imageEl?.hidden) {
          if (now - this.lastTap < 280) {
            this.toggleZoom(touch.clientX, touch.clientY);
            this.lastTap = 0;
            this.revealUi();
            return;
          }
          this.lastTap = now;
        }

        if (wasDraggingZoom) return;
        if (this.zoom.scale > 1.01) return;

        if (absY > absX && dy > 80) {
          this.close();
          return;
        }

        if (this.config.swipeEnabled !== false && absX > absY && absX > 50) {
          if (dx < 0) window.LeraGalleryNav?.next(this.gallery);
          else window.LeraGalleryNav?.prev(this.gallery);
          this.revealUi();
        }
      }
    }
  }

  function ensureController(root) {
    let instance = controllers.get(root);
    if (!instance) {
      instance = new LeraPremiumGalleryViewerController(root);
      instance.bind();
    }
    return controllers.get(root) || instance;
  }

  function boot() {
    document.querySelectorAll('[data-lera-pgv]').forEach((root) => ensureController(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);

  window.LeraPremiumGalleryViewer = {
    open(options = {}) {
      const gallery =
        options.gallery || document.querySelector('media-gallery.lera-product-gallery');
      const root =
        gallery?.closest('product-info')?.querySelector('[data-lera-pgv]') ||
        document.querySelector('[data-lera-pgv]');
      if (!root) return;
      ensureController(root)?.open(options);
    },
    close() {
      activeController?.close();
    },
  };
})();
